import { CheckpointCheck, InspectionCheckpointCheckResult } from '@/types/CheckpointCheck.type';

export const CHECKPOINT_CHECKS_COMPONENT_NAME = 'CheckpointChecks';

function toSnakeCase(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1_$2')
    .toLowerCase();
}

export function getActiveChecksForCheckpoint(
  checks: ReadonlyArray<CheckpointCheck>,
  templateId: string | undefined,
  checkpointCode: string
): CheckpointCheck[] {
  if (!templateId) {
    return [];
  }
  return checks
    .filter(
      (check) => check.isActive && check.templateId === templateId && check.code === checkpointCode
    )
    .slice()
    .sort((left, right) => {
      if (left.isParent !== right.isParent) {
        return left.isParent ? -1 : 1;
      }
      return left.checkCode.localeCompare(right.checkCode);
    });
}

export function hasNestedCheckpointChecks(
  checks: ReadonlyArray<CheckpointCheck>,
  templateId: string | undefined,
  checkpointCode: string
): boolean {
  return getActiveChecksForCheckpoint(checks, templateId, checkpointCode).length > 0;
}

export function isCheckpointCheckChecked(
  results: ReadonlyArray<InspectionCheckpointCheckResult> | undefined,
  checkpointCheckId: string
): boolean {
  return Boolean(
    results?.find((result) => result.checkpointCheckId === checkpointCheckId)?.isChecked
  );
}

export function isCheckpointComplete(params: {
  checks: ReadonlyArray<CheckpointCheck>;
  results: ReadonlyArray<InspectionCheckpointCheckResult> | undefined;
  templateId: string | undefined;
  checkpointCode: string;
}): boolean {
  const checkpointChecks = getActiveChecksForCheckpoint(
    params.checks,
    params.templateId,
    params.checkpointCode
  );
  if (checkpointChecks.length === 0) {
    return false;
  }
  const mandatoryChecks = checkpointChecks.filter((check) => check.isMandatory);
  const requiredChecks = mandatoryChecks.length > 0 ? mandatoryChecks : checkpointChecks;
  return requiredChecks.every((check) => isCheckpointCheckChecked(params.results, check.id));
}

export function withCheckpointCheckResult(
  results: ReadonlyArray<InspectionCheckpointCheckResult> | undefined,
  checkpointCheckId: string,
  isChecked: boolean
): InspectionCheckpointCheckResult[] {
  const next = [...(results ?? [])];
  const index = next.findIndex((result) => result.checkpointCheckId === checkpointCheckId);
  if (index === -1) {
    next.push({ checkpointCheckId, isChecked });
    return next;
  }
  next[index] = { ...next[index], isChecked };
  return next;
}

export function withParentCheckpointChecked(params: {
  checks: ReadonlyArray<CheckpointCheck>;
  results: ReadonlyArray<InspectionCheckpointCheckResult> | undefined;
  templateId: string | undefined;
  checkpointCode: string;
  isChecked: boolean;
}): InspectionCheckpointCheckResult[] | undefined {
  const parent = getActiveChecksForCheckpoint(
    params.checks,
    params.templateId,
    params.checkpointCode
  ).find((check) => check.isParent);
  if (!parent) {
    return params.results ? [...params.results] : undefined;
  }
  return withCheckpointCheckResult(params.results, parent.id, params.isChecked);
}

export function getCheckpointCheckTranslationKey(check: CheckpointCheck): string {
  return `${toSnakeCase(check.code)}_${toSnakeCase(check.checkCode)}`;
}
